<template>
  <section aria-labelledby="app-settings-title">
    <h2
      id="app-settings-title"
      class="text-h5 mb-4"
    >
      {{ $t("\n      App Settings\n    ") }}
    </h2>
    <v-alert
      v-if="error"
      type="error"
      outlined
    >
      {{ error }}
      <v-btn
        v-if="!settings"
        text
        small
        :loading="loading"
        @click="load"
      >
        {{ $t("\n        Try again\n      ") }}
      </v-btn>
    </v-alert>
    <v-alert
      v-if="message"
      type="success"
      outlined
      dismissible
    >
      {{ message }}
    </v-alert>
    <v-progress-linear
      v-if="loading"
      indeterminate
      :aria-label="$t(&quot;Loading app settings&quot;)"
    />
    <v-card
      v-if="settings"
      outlined
      class="pa-5 app-settings-card"
    >
      <h3 class="text-h6 mb-2">
        {{ $t("\n        Affiliate applications\n      ") }}
      </h3>
      <v-switch
        v-model="required"
        color="primary"
        :label="$t(&quot;Require a paid subscription to apply as an affiliate&quot;)"
        :disabled="busy || loading"
        hide-details
        class="mt-2 mb-3"
      />
      <p v-if="required">
        {{ $t("\n        New applicants must have an active paid subscription. Later plan changes do not affect existing applications, affiliate status or commissions.\n      ") }}
      </p>
      <p v-else>
        {{ $t("\n        Free accounts can apply. Administrator approval is required before an affiliate link becomes available.\n      ") }}
      </p>
      <v-btn
        color="primary"
        :loading="busy"
        :disabled="!changed || loading"
        @click="save"
      >
        {{ $t("\n        Save settings\n      ") }}
      </v-btn>
    </v-card>
    <v-card
      v-if="videoLimits"
      outlined
      class="pa-5 mt-5 app-settings-card"
    >
      <h3 class="text-h6 mb-2">
        {{ $t("\n        Video uploads\n      ") }}
      </h3>
      <p>{{ $t("Videos upload directly to private temporary storage. One video can be uploaded or processed per account at a time. Only the optimized video and its preview count toward account storage.") }}</p>
      <v-text-field
        v-model.number="videoLimits.maxUploadMB"
        type="number"
        min="100"
        max="2048"
        :label="$t(&quot;Maximum file size (MB, up to 2048)&quot;)"
      />
      <v-text-field
        v-model.number="videoLimits.maxDurationMinutes"
        type="number"
        min="1"
        max="20"
        :label="$t(&quot;Maximum video duration (minutes, up to 20)&quot;)"
      />
      <v-text-field
        v-model.number="videoLimits.dailyMinutes"
        type="number"
        min="1"
        max="600"
        :label="$t(&quot;Daily processing allowance per account (minutes)&quot;)"
      />
      <p class="text-caption">
        {{ $t("\n        Output: MP4, up to 1080p and 30 fps. Failed conversions also count toward the daily processing allowance. Unfinished temporary media expires automatically.\n      ") }}
      </p>
      <v-btn
        color="primary"
        :loading="videoBusy"
        @click="saveVideoLimits"
      >
        {{ $t("\n        Save video limits\n      ") }}
      </v-btn>
    </v-card>
  </section>
</template>
<script>
import { t } from '@/i18n'

import referrals from '@/services/referrals'
import { videoSettings } from '@/services/videoUploads'

export default {
  name: 'AppSettings',
  data: () => ({
    settings: null,
    required: false,
    loading: false,
    busy: false,
    error: '',
    message: '',
    videoLimits: null,
    videoBusy: false,
  }),
  computed: {
    changed() { return this.settings && this.required !== this.settings.affiliate_subscription_required },
  },
  mounted() { this.load(); this.loadVideoLimits() },
  methods: {
    async loadVideoLimits() {
      try { this.videoLimits = await videoSettings() } catch (error) { this.error = (error.response && error.response.data.message) || t('Video settings are unavailable.') }
    },
    async saveVideoLimits() {
      this.videoBusy = true; this.error = ''; this.message = ''
      try { this.videoLimits = await videoSettings(this.videoLimits); this.message = t('Video limits saved.') } catch (error) { this.error = (error.response && error.response.data.message) || t('Could not save video limits.') } finally { this.videoBusy = false }
    },
    async load() {
      this.loading = true; this.error = ''
      try {
        this.settings = await referrals.admin('app-settings')
        this.required = this.settings.affiliate_subscription_required
      } catch (error) {
        this.error = (error.response && error.response.data.message) || t('App settings are temporarily unavailable.')
      } finally { this.loading = false }
    },
    async save() {
      if (!this.changed || this.busy) return
      this.busy = true; this.error = ''; this.message = ''
      try {
        this.settings = await referrals.updateSettings({ affiliate_subscription_required: this.required })
        this.required = this.settings.affiliate_subscription_required
        this.message = t('App settings saved.')
      } catch (error) {
        this.error = (error.response && error.response.data.message) || t('Could not save app settings.')
      } finally { this.busy = false }
    },
  },
}
</script>
<style scoped>
.app-settings-card { max-width: 840px; }
</style>
